// ============================================================================
// ENTERPRISE AI ORCHESTRATOR API CLIENT
// ============================================================================
// Deterministic compliance execution interface - No legacy assistant routes
// ============================================================================

import { ApiClient } from '../../core/apiClient';

export interface AIOrchestrationRequest {
  user_message: string;
  conversation_id?: string;
}

export interface QuickAction {
  id: string;
  label: string;
  description: string;
  icon?: string;
  capability: string;
  params?: any;
  requiresConfirmation?: boolean;
  estimatedTime?: string;
  category?: 'action' | 'info' | 'navigation';
}

export interface ProactiveAlert {
  id: string;
  type: 'deadline' | 'compliance_drift' | 'team_overload' | 'risk_spike' | 'regulatory_update' | 'framework_update';
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  message: string;
  suggestedActions?: QuickAction[];
  affectedResources?: string[];
  expiresAt?: string;
  dismissible: boolean;
  createdAt: string;
}

export interface ProactiveAlertsResponse {
  alerts: ProactiveAlert[];
  unreadCount: number;
}

export interface SmartSuggestion {
  id: string;
  title: string;
  description: string;
  category: 'automation' | 'optimization' | 'compliance' | 'reporting';
  confidence: number;
  potentialImpact?: {
    timeSaved?: string;
    riskReduced?: string;
    costSaved?: string;
  };
  action: {
    capability: string;
    params: any;
    requiresConfirmation?: boolean;
  };
}

export interface AICapability {
  task_name: string;
  description: string;
  scope: 'system' | 'company' | 'user';
  allowed_personas: string[];
  risk_level: 'low' | 'medium' | 'high';
  confirmation_token_required: boolean;
  category?: string;
  keywords?: string[];
}

export interface Citation {
  url?: string;
  title?: string;
  source?: string;
  publishedAt?: string;
}

export interface AITool {
  id: string;
  name: string;
  description: string;
  category: 'internal' | 'external' | 'compliance';
  type: 'orchestrator' | 'news' | 'web' | 'regulatory';
  capabilities: Array<{
    name: string;
    description: string;
    riskLevel: 'low' | 'medium' | 'high';
    requiresConfirmation: boolean;
  }>;
  available: boolean;
  icon: string;
  estimatedTime: string;
}

export interface AvailableToolsResponse {
  success: boolean;
  tools: AITool[];
  error?: string;
}

export interface UnifiedBrainRequest {
  query: string;
  conversation_id?: string;
  previous_queries?: string[];
  max_sources?: number;
  confidence_threshold?: number;
  include_sources?: boolean;
  selected_tools?: string[];
}

export interface UnifiedBrainResponse {
  success: boolean;
  answer: string;
  confidence: number; // 0-1
  sources: Array<{
    type: 'internal' | 'news' | 'web' | 'regulatory';
    name: string;
    confidence: number;
    citations?: Citation[];
  }>;
  metadata: {
    query_type: string;
    processing_time: number;
    aggregated: boolean;
  };
  error?: string;
}

// Keep old interface for backward compatibility during transition
export interface AIOrchestrationResponse {
  response_type: 'executed' | 'confirmation_required' | 'denied' | 'error' | 'data';
  message: string;
  confirmation_data?: {
    confirmation_id: string;
    confirmation_hash: string;
    expires_at: string;
    risk_level: 'low' | 'medium' | 'high';
    task_name: string;
  };
  execution_result?: any;
  audit_log_id?: string;
  execution_time_ms?: number;
}

export interface AIHistoryResponse {
  messages: Array<{
    id: string;
    role: 'user' | 'assistant' | 'system';
    content: string;
    timestamp: string;
    meta?: {
      confirmationId?: string;
      riskLevel?: 'low' | 'medium' | 'high';
      auditLogId?: string;
      executionTimeMs?: number;
      companyId?: number | null;
    };
  }>;
  pagination?: {
    total: number;
    limit: number;
    offset: number;
  };
}

export interface AIConversationSummary {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

export interface AIConversationsResponse {
  conversations: AIConversationSummary[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

export interface AIConversationMessagesResponse {
  messages: Array<{
    id: string;
    role: 'user' | 'assistant' | 'system';
    content: string;
    created_at: string;
    metadata?: Record<string, unknown>;
  }>;
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

export interface AICapabilitiesResponse {
  capabilities: Array<{
    name: string;
    description: string;
    risk_level: 'low' | 'medium' | 'high';
    requires_confirmation: boolean;
  }>;
  supported_personas: string[];
}

export interface SimulateComplianceOutcomeParams {
  companyId: number;
  frameworkId: string;
  timeframe: '30d' | '90d' | '180d';
  includeRecommendations?: boolean;
}

export interface SimulatedComplianceOutcome {
  predictedScore: number;
  confidence: number;
  factors: Array<{
    name: string;
    impact: 'positive' | 'negative' | 'neutral';
    weight: number;
  }>;
  recommendations: string[];
}

export interface ValidateEvidenceParams {
  evidence_id: number;
  expected_content: string;
}

export interface EvidenceValidationResult {
  validation_status: 'pass' | 'fail' | 'needs_review';
  extracted_data: Record<string, unknown>;
  issues: Array<{
    title: string;
    message: string;
    source: 'validation' | 'ai' | 'ocr';
    suggestions: string[];
  }>;
}

class AiApi extends ApiClient {
  constructor() {
    super({
      enableLogging: import.meta.env.DEV,
    });
  }

  /**
   * Get available AI tools for dynamic selection
   */
  async getAvailableTools(): Promise<AvailableToolsResponse> {
    const response = await this.client.get('/ai/capabilities/tools');
    return response.data;
  }

  /**
   * Process AI request through unified brain with optional tool selection
   */
  async processAIRequestWithTools(
    userMessage: string,
    conversationId?: string,
    selectedTools?: string[]
  ): Promise<AIOrchestrationResponse> {
    const response = await this.client.post('/ai/brain/process', {
      query: userMessage,
      ...(conversationId ? { conversation_id: conversationId } : {}),
      ...(selectedTools ? { selected_tools: selectedTools } : {})
    });

    // Transform unified brain response to maintain backward compatibility
    const brainData = response.data;
    if (brainData.success) {
      return {
        ...brainData,
        // Add backward compatibility fields
        message: brainData.answer, // Map answer to message for compatibility
        response_type: 'executed' as const,
        execution_time_ms: brainData.metadata?.processing_time,
        audit_log_id: undefined // Unified brain doesn't have audit logs yet
      };
    }

    return brainData;
  }

  /**
   * Process AI request through unified brain with full options
   */
  async processUnifiedBrainRequest(request: UnifiedBrainRequest): Promise<UnifiedBrainResponse> {
    const response = await this.client.post('/ai/brain/process', request);
    return response.data;
  }

  /**
   * Process AI request through orchestrator
   * Single execution path - no fallback logic
   */
  async processAIRequest(userMessage: string, conversationId?: string): Promise<AIOrchestrationResponse> {
    try {
      // Try orchestrator first (preserve existing behavior)
      return await this.processAIRequestViaOrchestrator(userMessage, conversationId);
    } catch (error: any) {
      // Fallback to unified brain only if orchestrator fails
      console.warn('Orchestrator failed, falling back to unified brain:', error.message);
      try {
        const response = await this.client.post('/ai/brain/process', {
          query: userMessage,
          ...(conversationId ? { conversation_id: conversationId } : {})
        });

        // Transform unified brain response to maintain backward compatibility
        const brainData = response.data;
        if (brainData.success) {
          return {
            ...brainData,
            // Add backward compatibility fields
            message: brainData.answer, // Map answer to message for compatibility
            response_type: 'executed' as const,
            execution_time_ms: brainData.metadata?.processing_time,
            audit_log_id: undefined // Unified brain doesn't have audit logs yet
          };
        } else {
          return {
            ...brainData,
            message: brainData.error || 'An error occurred',
            response_type: 'error' as const,
            execution_time_ms: brainData.metadata?.processing_time
          };
        }
      } catch (brainError: any) {
        console.error('Both orchestrator and unified brain failed:', brainError.message);
        // Return the original orchestrator error
        throw error;
      }
    }
  }

  // Cache the persona lookup for the duration of this client instance.
  // The agent never changes mid-session for a given login.
  private agentPersonaCache: { persona: string; implemented: boolean } | null = null;
  // Map a legacy session conversation id (string) to the agent backend's
  // numeric conversation_id. We need this because the legacy chat hook
  // uses opaque string ids while the agent endpoint expects integers.
  private agentConversationMap = new Map<string, number>();
  // The most recent agent conversation id created in this session — used
  // when the legacy hook calls without a conversationId on a fresh chat.
  private lastAgentConversationId: number | null = null;

  /**
   * Look up the user's persona once, cache the result. Returns null if
   * the user is not authenticated or the persona has no dedicated agent.
   */
  private async getAgentPersonaInfo(): Promise<{ persona: string; implemented: boolean } | null> {
    if (this.agentPersonaCache) return this.agentPersonaCache;
    try {
      const res = await this.client.get('/ai/agent/persona/current');
      const data = res.data?.data;
      if (!data) return null;
      this.agentPersonaCache = { persona: data.persona, implemented: !!data.implemented };
      return this.agentPersonaCache;
    } catch {
      return null;
    }
  }

  /**
   * Run a user message through the dedicated per-persona agent. Returns
   * a synthetic AIOrchestrationResponse so the legacy chat UI can display
   * it unchanged. Returns null if no agent is implemented for this persona.
   */
  private async processViaPersonaAgent(
    userMessage: string,
    legacyConversationId: string | undefined,
    onChunk: (chunk: string) => void,
    _signal?: AbortSignal,
  ): Promise<AIOrchestrationResponse | null> {
    const personaInfo = await this.getAgentPersonaInfo();
    if (!personaInfo || !personaInfo.implemented) return null;

    // Resolve agent conversation id. If the legacy hook passed a session
    // string id we've already mapped, reuse it. Otherwise create a new
    // agent conversation and remember the mapping.
    let agentConversationId: number | null = null;
    if (legacyConversationId && this.agentConversationMap.has(legacyConversationId)) {
      agentConversationId = this.agentConversationMap.get(legacyConversationId)!;
    } else if (legacyConversationId && this.lastAgentConversationId) {
      // Same session, no mapping yet — reuse the most recent agent conv.
      agentConversationId = this.lastAgentConversationId;
      this.agentConversationMap.set(legacyConversationId, agentConversationId);
    } else {
      const createRes = await this.client.post('/ai/agent/conversations', {});
      agentConversationId = createRes.data?.data?.conversation?.id;
      if (!agentConversationId) return null;
      this.lastAgentConversationId = agentConversationId;
      if (legacyConversationId) this.agentConversationMap.set(legacyConversationId, agentConversationId);
    }

    // NOTE: do NOT call onChunk('') here. The legacy chat hook treats the
    // first chunk as a signal to materialize a streaming placeholder bubble,
    // which then sits empty for the duration of the agent's tool loop and
    // shows as a blank pill above the "Composing response..." indicator.
    // The hook already shows its own status spinner via setStatusMessage —
    // we don't need to fake a chunk to trigger it.

    // Persist the user message to the LEGACY conversation store BEFORE the
    // agent call. The legacy chat hook reads history from ai_messages on
    // reload — without this dual-write, agent conversations vanish on
    // refresh because they only live in ai_agent_messages.
    if (legacyConversationId) {
      this.appendConversationMessage(legacyConversationId, 'user', userMessage).catch(() => { /* non-fatal */ });
    }

    const chatRes = await this.client.post('/ai/agent/chat', {
      conversation_id: agentConversationId,
      message: userMessage,
    });
    const data = chatRes.data?.data;
    if (!data) return null;

    const finalMessage = data.finalMessage || '(no response)';

    // Mirror the assistant reply to the legacy store too, so reload restores
    // the full thread.
    if (legacyConversationId) {
      this.appendConversationMessage(legacyConversationId, 'assistant', finalMessage, {
        agent_conversation_id: agentConversationId,
        tool_calls: data.toolCalls || [],
        steps: data.steps,
        finish_reason: data.finishReason,
      }).catch(() => { /* non-fatal */ });
    }

    return {
      response_type: 'executed',
      message: finalMessage,
      execution_result: {
        agent_conversation_id: agentConversationId,
        tool_calls: data.toolCalls || [],
        steps: data.steps,
        finish_reason: data.finishReason,
      },
      execution_time_ms: undefined,
    };
  }

  /**
   * Process AI request with streaming response
   * Streams response chunks as they arrive
   *
   * Routing: if the user's persona has a dedicated agent implemented at
   * /api/ai/agent/*, we use the new per-persona agent path (Aegis / Atlas /
   * Forge / Sentinel / Veritas / Apex). Otherwise we fall back to the
   * legacy SSE orchestrator stream. This lets the existing AIAssistant
   * widget keep working AND lets each user automatically get their
   * persona-scoped agent without a separate UI.
   */
  async processAIRequestStreaming(
    userMessage: string,
    conversationId: string | undefined,
    onChunk: (chunk: string) => void,
    signal?: AbortSignal
  ): Promise<AIOrchestrationResponse> {
    // Try the dedicated per-persona agent first.
    try {
      const agentResponse = await this.processViaPersonaAgent(userMessage, conversationId, onChunk, signal);
      if (agentResponse) return agentResponse;
    } catch (e: any) {
      // Genuine network/auth errors propagate; "no agent for persona" returns
      // null and we fall through to the legacy stream.
      if (e?.name === 'AbortError') throw e;
      console.warn('[aiApi] persona agent path failed, falling back to legacy stream:', e?.message);
    }

    return new Promise((resolve, reject) => {
      const token = localStorage.getItem('token');
      // Use the orchestrator streaming endpoint (GET /api/ai/orchestrate/stream)
      // which sends SSE progress events during execution and a final 'complete' event.
      const eventSource = new EventSource(
        `/api/ai/orchestrate/stream?user_message=${encodeURIComponent(userMessage)}` +
        `${conversationId ? `&conversation_id=${conversationId}` : ''}` +
        `${token ? `&token=${encodeURIComponent(token)}` : ''}`
      );

      // Support abort via AbortSignal
      if (signal) {
        if (signal.aborted) {
          eventSource.close();
          reject(new DOMException('Aborted', 'AbortError'));
          return;
        }
        signal.addEventListener('abort', () => {
          eventSource.close();
          reject(new DOMException('Aborted', 'AbortError'));
        });
      }

      let fullResponse: AIOrchestrationResponse | null = null;
      let streamingFailed = false;

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'chunk') {
            // Text chunk (future use — orchestrator may stream text)
            onChunk(data.content);
          } else if (data.type === 'progress') {
            // Plan execution progress — surface as a status hint via onChunk
            if (data.message) {
              onChunk('');  // trigger the streaming indicator without appending text
            }
          } else if (data.type === 'complete') {
            // Orchestrator sends { type: 'complete', response: <AIOrchestrationResponse> }
            // The response already has: message, response_type, confirmation_data, etc.
            const orchestratorResponse = data.response as AIOrchestrationResponse;
            fullResponse = orchestratorResponse;
            eventSource.close();
            if (fullResponse) {
              resolve(fullResponse);
            } else {
              reject(new Error('No response received'));
            }
          } else if (data.type === 'error') {
            eventSource.close();
            reject(new Error(data.message || 'Streaming error'));
          }
        } catch (error) {
          console.error('Failed to parse streaming data:', error);
        }
      };

      eventSource.onerror = (_event) => {
        if (!streamingFailed) {
          streamingFailed = true;
          eventSource.close();
          // Fallback to regular (non-streaming) request
          this.processAIRequest(userMessage, conversationId)
            .then(resolve)
            .catch(reject);
        }
      };

      // Timeout after 5 minutes
      setTimeout(() => {
        if (!streamingFailed) {
          streamingFailed = true;
          eventSource.close();
          reject(new Error('Streaming timeout'));
        }
      }, 5 * 60 * 1000);
    });
  }

  /**
   * Process AI request through orchestrator (fallback method)
   * Used when unified brain fails
   */
  async processAIRequestViaOrchestrator(userMessage: string, conversationId?: string): Promise<AIOrchestrationResponse> {
    const response = await this.client.post('/ai/orchestrate', {
      user_message: userMessage,
      ...(conversationId ? { conversation_id: conversationId } : {})
    });
    return response.data;
  }

  /**
   * Execute confirmed high-risk task
   * Cryptographically validated execution
   */
  async executeConfirmedTask(confirmationId: string, confirmationHash: string): Promise<AIOrchestrationResponse> {
    const response = await this.client.post('/ai/execute-confirmed', {
      confirmation_id: confirmationId,
      confirmation_hash: confirmationHash,
    });
    return response.data;
  }

  /**
   * Get conversation history for company
   * Server-side persistence with audit compliance
   */
  async getConversationHistory(companyId: number, limit: number = 50, offset: number = 0): Promise<AIHistoryResponse> {
    const params = new URLSearchParams({
      companyId: companyId.toString(),
      limit: limit.toString(),
      offset: offset.toString(),
    });
    const response = await this.client.get(`/ai/history?${params.toString()}`);
    return response.data;
  }

  async createConversation(title?: string, welcomeMessage?: string): Promise<AIConversationSummary> {
    const response = await this.client.post('/ai/conversations', {
      ...(title ? { title } : {}),
      ...(welcomeMessage ? { welcome_message: welcomeMessage } : {})
    });
    return response.data.data;
  }

  async listConversations(limit: number = 20, offset: number = 0): Promise<AIConversationsResponse> {
    const response = await this.client.get('/ai/conversations', {
      params: { limit, offset }
    });
    return {
      conversations: response.data.conversations || [],
      pagination: response.data.pagination || { total: 0, limit, offset }
    };
  }

  async getConversationMessages(conversationId: string, limit: number = 100, offset: number = 0): Promise<AIConversationMessagesResponse> {
    const response = await this.client.get(`/ai/conversations/${conversationId}/messages`, {
      params: { limit, offset }
    });
    return {
      messages: response.data.messages || [],
      pagination: response.data.pagination || { total: 0, limit, offset }
    };
  }

  async deleteConversation(conversationId: string): Promise<void> {
    await this.client.delete(`/ai/conversations/${conversationId}`);
  }

  async updateConversationTitle(conversationId: string, title: string): Promise<void> {
    await this.client.patch(`/ai/conversations/${conversationId}/title`, { title });
  }

  async appendConversationMessage(
    conversationId: string,
    role: 'user' | 'assistant' | 'system',
    content: string,
    metadata?: Record<string, unknown>
  ): Promise<{ id: string; role: 'user' | 'assistant' | 'system'; content: string; created_at: string; metadata?: Record<string, unknown> }> {
    const response = await this.client.post(`/ai/conversations/${conversationId}/messages`, {
      role,
      content,
      ...(metadata ? { metadata } : {})
    });
    return response.data.data;
  }

  /**
   * Get available AI capabilities
   * Persona-aware capability enumeration
   */
  async getCapabilities(): Promise<AICapabilitiesResponse> {
    const response = await this.client.get('/ai/capabilities');
    return response.data;
  }

  /**
   * Get contextual quick actions for current page/context
   */
  async getContextualActions(context: {
    page: string;
    selection?: any[];
    userPermissions?: string[];
  }): Promise<QuickAction[]> {
    const response = await this.client.post('/ai/contextual-actions', context);
    return response.data.actions || [];
  }

  /**
   * Get proactive alerts for current user/company
   */
  async getProactiveAlerts(): Promise<ProactiveAlertsResponse> {
    const response = await this.client.get('/ai/proactive-alerts');
    return response.data;
  }

  /**
   * Dismiss a proactive alert
   */
  async dismissProactiveAlert(alertId: string): Promise<void> {
    await this.client.post(`/ai/proactive-alerts/${alertId}/dismiss`);
  }

  /**
   * Get smart suggestions based on current context
   */
  async getSuggestions(context?: any): Promise<SmartSuggestion[]> {
    const response = await this.client.post('/ai/suggestions', { context });
    return response.data.suggestions || [];
  }

  /**
   * Execute a capability directly (for quick actions)
   */
  async executeCapability(capability: string, params: any): Promise<AIOrchestrationResponse> {
    const response = await this.client.post('/ai/execute-capability', {
      capability,
      params
    });
    return response.data;
  }

  /**
   * Get available capabilities filtered by context
   */
  async getAvailableCapabilities(context?: string): Promise<AICapability[]> {
    const response = await this.client.get('/ai/capabilities/list', {
      params: context ? { context } : {}
    });
    const payload = response.data;
    const listPayload = Array.isArray(payload)
      ? payload
      : (Array.isArray(payload?.capabilities) ? payload.capabilities : []);

    const normalize = (rows: any[]): AICapability[] => rows
      .map((cap: any) => {
        const taskName = String(cap?.task_name || cap?.name || cap?.taskName || '').trim();
        if (!taskName) return null;

        const requiresConfirmation = cap?.confirmation_token_required ??
          cap?.requires_confirmation ??
          cap?.requiresConfirmation ??
          false;
        const riskLevel = cap?.risk_level || cap?.riskLevel || 'low';

        return {
          task_name: taskName,
          description: cap?.description || 'No description available',
          scope: cap?.scope || 'company',
          allowed_personas: Array.isArray(cap?.allowed_personas)
            ? cap.allowed_personas
            : (typeof cap?.required_persona === 'string' ? [cap.required_persona] : []),
          risk_level: riskLevel,
          confirmation_token_required: Boolean(requiresConfirmation),
          category: cap?.category,
          keywords: Array.isArray(cap?.keywords) ? cap.keywords : undefined,
        } as AICapability;
      })
      .filter(Boolean) as AICapability[];

    let normalized = normalize(listPayload);
    if (normalized.length > 0) return normalized;

    // Backward-compatible fallback when list endpoint shape drifts.
    const fallbackResponse = await this.client.get('/ai/capabilities');
    const fallbackRows = Array.isArray(fallbackResponse.data?.capabilities)
      ? fallbackResponse.data.capabilities
      : [];
    normalized = normalize(fallbackRows);
    return normalized;
  }

  /**
   * Deterministic client-side simulation using real analytics history endpoint as source data.
   */
  async simulateComplianceOutcome(params: SimulateComplianceOutcomeParams): Promise<SimulatedComplianceOutcome> {
    const response = await this.client.get('/analytics/compliance-score/history', {
      params: {
        companyId: params.companyId,
        frameworkId: params.frameworkId,
        limit: params.timeframe === '30d' ? 8 : params.timeframe === '90d' ? 16 : 24
      }
    });

    const history = Array.isArray(response.data?.data) ? response.data.data : [];
    const scores = history
      .map((item: any) => Number(item.score ?? item.compliance_score ?? 0))
      .filter((value: number) => Number.isFinite(value));

    const latest = scores.length > 0 ? scores[0]! : 70;
    const avg = scores.length > 0 ? scores.reduce((a: number, b: number) => a + b, 0) / scores.length : latest;
    const drift = latest - avg;
    const predictedScore = Math.max(0, Math.min(100, latest + drift * 0.25));

    return {
      predictedScore,
      confidence: scores.length >= 5 ? 0.82 : 0.7,
      factors: [
        { name: 'Recent compliance trend', impact: drift >= 0 ? 'positive' : 'negative', weight: 0.4 },
        { name: 'Historical average stability', impact: 'neutral', weight: 0.25 },
        { name: 'Evidence submission consistency', impact: 'neutral', weight: 0.2 },
        { name: 'Review throughput', impact: drift >= 0 ? 'positive' : 'negative', weight: 0.15 }
      ],
      recommendations: params.includeRecommendations === false
        ? []
        : [
            'Increase submission cadence for overdue controls',
            'Prioritize high-risk pages for review',
            'Close open audit findings tied to low-scoring sections'
          ]
    };
  }

  /**
   * Deterministic evidence validation projection backed by live compliance evidence list.
   */
  async validateEvidence(params: ValidateEvidenceParams): Promise<EvidenceValidationResult> {
    const response = await this.client.get('/compliance/execution/company/evidence');
    const evidenceList = Array.isArray(response.data?.data) ? response.data.data : [];
    const evidence = evidenceList.find((item: any) => Number(item.id) === Number(params.evidence_id));

    if (!evidence) {
      return {
        validation_status: 'needs_review',
        extracted_data: {},
        issues: [
          {
            title: 'Evidence lookup incomplete',
            message: `Evidence record ${params.evidence_id} was not found in company evidence scope`,
            source: 'validation',
            suggestions: ['Confirm evidence upload completed', 'Verify company access scope']
          }
        ]
      };
    }

    const status = String(evidence.status || '').toLowerCase();
    const pass = status === 'submitted' || status === 'internal_verified';

    return {
      validation_status: pass ? 'pass' : 'needs_review',
      extracted_data: {
        id: evidence.id,
        file_name: evidence.file_name,
        status: evidence.status,
        uploaded_by: evidence.uploaded_by,
        expected_content: params.expected_content
      },
      issues: pass
        ? []
        : [
            {
              title: 'Evidence not yet verified',
              message: 'Evidence exists but is not in a submitted/verified state',
              source: 'validation',
              suggestions: ['Submit evidence for review', 'Add required notes and metadata']
            }
          ]
    };
  }
}

export const aiApi = new AiApi();
export default aiApi;
